import { useAppSelector } from '@hooks/useRedux';
import {
  useRoomTypes,
  useRooms,
  useUpdateRoom,
  useUpdateRoomType,
} from '@hooks/accommodation';
import { selectAccommodationProperty } from '@store/accommodation';
import { BedDouble, Edit2, Plus, PowerOff, Power } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AccommodationRoom,
  AccommodationRoomType,
} from '../../../../types/accommodation';
import { RoomFormModal } from './RoomFormModal';
import { RoomTypeFormModal } from './RoomTypeFormModal';

export const RoomsPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const { data: allRoomTypes = [], isLoading } = useRoomTypes(property?.id);
  const { data: allRooms = [] } = useRooms(property?.id);
  const updateRoomType = useUpdateRoomType();
  const updateRoom = useUpdateRoom();

  const roomTypes = useMemo(
    () => allRoomTypes.filter((rt) => rt.is_active),
    [allRoomTypes],
  );

  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<AccommodationRoomType | null>(
    null,
  );
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState<AccommodationRoom | null>(
    null,
  );
  const [showRoomForm, setShowRoomForm] = useState(false);

  // Select first room type by default when types load
  useEffect(() => {
    if (!selectedTypeId && roomTypes.length > 0) {
      setSelectedTypeId(roomTypes[0].id);
    }
  }, [roomTypes, selectedTypeId]);

  const roomsForType = useMemo(
    () =>
      selectedTypeId
        ? allRooms.filter((r) => r.room_type_id === selectedTypeId)
        : [],
    [allRooms, selectedTypeId],
  );

  const selectedType = useMemo(
    () => roomTypes.find((rt) => rt.id === selectedTypeId) ?? null,
    [roomTypes, selectedTypeId],
  );

  const handleDeactivateType = (rt: AccommodationRoomType) => {
    updateRoomType.mutate(
      { id: rt.id, updates: { is_active: false } },
      {
        onSuccess: () => {
          if (selectedTypeId === rt.id) setSelectedTypeId(null);
        },
      },
    );
  };

  const handleToggleRoom = (room: AccommodationRoom) => {
    updateRoom.mutate({ id: room.id, updates: { is_active: !room.is_active } });
  };

  if (!property) return null;

  return (
    <div className="flex gap-5">
      {/* Left: room types */}
      <div className="w-64 shrink-0 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Room types</h3>
          <button
            type="button"
            onClick={() => {
              setEditingType(null);
              setShowTypeForm(true);
            }}
            className="flex items-center gap-1 rounded-lg bg-pos-primary px-2.5 py-1.5 text-xs font-medium text-white hover:bg-pos-primary/90"
          >
            <Plus size={12} />
            Add
          </button>
        </div>

        {isLoading && roomTypes.length === 0 ? (
          <p className="text-xs text-gray-400">Loading…</p>
        ) : roomTypes.length === 0 ? (
          <p className="text-xs text-gray-400">No room types yet.</p>
        ) : (
          <div className="space-y-1">
            {roomTypes.map((rt) => (
              <div
                key={rt.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedTypeId(rt.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedTypeId(rt.id);
                  }
                }}
                className={[
                  'group flex w-full items-start justify-between rounded-xl px-3 py-2.5 text-left transition-colors',
                  selectedTypeId === rt.id
                    ? 'bg-pos-primary/10 text-pos-primary'
                    : 'text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                <div>
                  <p className="text-sm font-medium leading-tight">{rt.name}</p>
                  <p className="mt-0.5 text-xs text-gray-400">
                    {rt.capacity} guest{rt.capacity !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="ml-2 mt-0.5 flex shrink-0 gap-1 opacity-0 group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingType(rt);
                      setShowTypeForm(true);
                    }}
                    className="rounded p-0.5 hover:bg-gray-200"
                    title="Edit"
                  >
                    <Edit2 size={11} />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeactivateType(rt);
                    }}
                    className="rounded p-0.5 hover:bg-gray-200"
                    title="Deactivate"
                  >
                    <PowerOff size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Right: rooms */}
      <div className="min-w-0 flex-1">
        {!selectedType ? (
          <div className="flex h-48 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
            Select a room type to manage its rooms
          </div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {selectedType.name}
                </h3>
                <p className="mt-0.5 text-xs text-gray-400">
                  Capacity: {selectedType.capacity} · Base:{' '}
                  {selectedType.base_price.toLocaleString()} / night
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingRoom(null);
                  setShowRoomForm(true);
                }}
                className="flex items-center gap-1.5 rounded-lg bg-pos-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-pos-primary/90"
              >
                <Plus size={13} />
                Add room
              </button>
            </div>

            {roomsForType.length === 0 ? (
              <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-gray-200 text-sm text-gray-400">
                No rooms for this type yet
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {roomsForType.map((room) => (
                  <div
                    key={room.id}
                    className={[
                      'rounded-xl border p-3',
                      room.is_active
                        ? 'border-gray-200'
                        : 'border-gray-100 opacity-50',
                    ].join(' ')}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <BedDouble
                          size={14}
                          className="mt-0.5 shrink-0 text-pos-primary"
                        />
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {room.room_number}
                          </p>
                          {room.floor && (
                            <p className="text-xs text-gray-400">
                              Floor {room.floor}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingRoom(room);
                            setShowRoomForm(true);
                          }}
                          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title="Edit"
                        >
                          <Edit2 size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleToggleRoom(room)}
                          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          title={room.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {room.is_active ? (
                            <PowerOff size={12} />
                          ) : (
                            <Power size={12} />
                          )}
                        </button>
                      </div>
                    </div>
                    {room.notes && (
                      <p className="mt-1.5 text-xs leading-snug text-gray-400">
                        {room.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showTypeForm && (
        <RoomTypeFormModal
          propertyId={property.id}
          editing={editingType}
          onClose={() => {
            setShowTypeForm(false);
            setEditingType(null);
          }}
        />
      )}

      {showRoomForm && (
        <RoomFormModal
          propertyId={property.id}
          editing={editingRoom}
          selectedRoomTypeId={selectedTypeId}
          onClose={() => {
            setShowRoomForm(false);
            setEditingRoom(null);
          }}
        />
      )}
    </div>
  );
};
